import schedulesViewStateStore from "sh-application/components/schedules/lib/store/store";
import { previewSchedulePageStore } from "sh-application/components/previewSchedulePage";
import shiftsModified from "sh-stores/sh-shift-store/actions/shiftsModified";
import ShiftUtils from "sh-application/utility/ShiftUtils";
import updateShiftInStore from "sh-stores/sh-shift-store/mutatorActions/updateShiftInStore";
import updateShiftsV2 from "sh-stores/sh-shift-store/actions/updateShiftsV2";
import { DataProcessingHelpers } from "sh-services";
import { IBaseShiftEntity, IShiftEntity, ShiftEntity } from "sh-models";
import { Moment } from "moment";
import { MyShiftStore, ShiftStore } from "..";
import { orchestrator } from "satcheljs";
import { ShiftStoreSchema } from "../store/schema/ShiftStoreSchema";
import { ShiftStoreTypes } from "sh-application/../StaffHubConstants";
import { trace } from "owa-trace";
import { updateShiftsInView } from "sh-application/components/schedules/lib";
import { IPreviewSchedulePageStore } from "../../../sh-application/components/previewSchedulePage";
import { SchedulesViewStateStore } from "../../../sh-application/components/schedules/lib/store/schema/SchedulesViewStateStore";

interface AddEligibleShiftsToViewParam {
    viewState: IPreviewSchedulePageStore | SchedulesViewStateStore;
    shiftsToProcess?: Array<IShiftEntity>;
    shiftStoreEntity?: ShiftStoreSchema;
}

/**
 * Checks if shifts that are already in the data store but not in the current view should be added to the view's cache
 * based on the view's date range, and adds them if they fall within the visible range.
 * @param param0
 */
const addEligibleShiftsToView = ({ viewState, shiftsToProcess, shiftStoreEntity }: AddEligibleShiftsToViewParam) => {
    const shiftsMaybeAddToView: Array<IShiftEntity> = [];
    shiftsToProcess.forEach((shiftModel: IShiftEntity) => {
        const shiftInStore: IShiftEntity = shiftStoreEntity.shifts.get(shiftModel.id);

        if (shiftInStore) {
            // Only process existing shifts that weren't already categorized as added/updated/deleted
            if (!ShiftUtils.hasItemBeenModifiedByService(shiftInStore, shiftModel)) {
                // this is the case where the shift is already in the memory cache, but it may not actually be in
                // the "in view" cache. To handle these shifts, we first check that they are not currently in the view cache
                // and later we will add them if they are in the viewable range.
                if (!viewState.shiftsInView.has(shiftModel.id)) {
                    shiftsMaybeAddToView.push(shiftModel);
                }
            }
        }
    });

    if (shiftsMaybeAddToView.length) {
        // we have shifts that may need to be added to the in view memory cache. Check if they are in our current view
        // range and if so, add them to the in view cache.
        const shiftsInRange: IBaseShiftEntity[] = DataProcessingHelpers.getDisplayableShiftsInScheduleRange(
            shiftsMaybeAddToView,
            viewState.viewStartDate,
            viewState.viewEndDate);
        if (shiftsInRange && shiftsInRange.length) {
            updateShiftsInView(shiftsInRange, viewState);
        }
    }
};

export default orchestrator(updateShiftsV2, async actionMessage => {
    let shiftsAdded: Array<IShiftEntity> = [];
    let shiftsUpdated: Array<IShiftEntity> = [];
    let shiftsDeleted: Array<IShiftEntity> = [];
    const viewStates = [previewSchedulePageStore(), schedulesViewStateStore()];

    let shiftStoreEntity: ShiftStoreSchema;

    actionMessage.shiftStoreType === ShiftStoreTypes.MyShiftStore ?
        shiftStoreEntity = MyShiftStore()
        :
        shiftStoreEntity = ShiftStore();

    const cacheStartTime: Moment = shiftStoreEntity.shiftsCacheStartTime;
    const cacheEndTime: Moment = shiftStoreEntity.shiftsCacheEndTime;

    // We use clones of the argument entities. This is because setting the entities in the satchel stores will mark them as observables
    // and any callers who still hold references to these entities will not be able to mutate them (in event of an error, for example) without
    // triggering mobx exceptions
    const shiftsToProcess: Array<IShiftEntity> = actionMessage.shiftsUpdated.map(shift => ShiftEntity.clone(shift));

    shiftsToProcess.forEach((shiftModel: IShiftEntity) => {
        let setShift = true;
        const shiftInStore: IShiftEntity = shiftStoreEntity.shifts.get(shiftModel.id);
        if (!shiftInStore) {
            // if this shiftModel is not already in the cache, this is an add.
            // validate that it fits within our cache range
            if (!ShiftUtils.shiftOverlapsStartsOrEndsBetween(shiftModel, cacheStartTime, cacheEndTime, false /*includeEdges*/)) {
                // this shift is not within our cache range, show a debug error
                trace.info("New shift is outside our cache date range");

                // if shift is not within cache range do not set shift in cache
                setShift = false;
            } else {
                // keep track of all the shifts added
                shiftsAdded.push(shiftModel);
            }
        } else {
            // Check if the shift has been modified by the service (or if it's an optimistic update where shift data has changed but etag hasn't)
            if (ShiftUtils.hasItemBeenModifiedByService(shiftInStore, shiftModel) || actionMessage.isOptimisticUpdate) {
                // keep track of all the shifts updated/deleted
                if (ShiftUtils.isDeletedShift(shiftModel)) {
                    shiftsDeleted.push(shiftModel);
                } else {
                    shiftsUpdated.push(shiftModel);
                }
            }
        }

        // set the shift in the cache (this will either add a new entry or update an existing one)
        if (setShift) {
            // Preserve server-set AOS tracking fields that some API responses don't include.
            // These read-only fields are only returned by getDataInDateRange and the share API;
            // other APIs (update-shift, etc.) may omit them, which would wipe the store entry.
            if (shiftInStore) {
                if (shiftModel.generationSource == null && shiftInStore.generationSource != null) {
                    shiftModel.generationSource = shiftInStore.generationSource;
                }
                if (shiftModel.isReassignedAfterGeneration == null && shiftInStore.isReassignedAfterGeneration != null) {
                    shiftModel.isReassignedAfterGeneration = shiftInStore.isReassignedAfterGeneration;
                }
                if (shiftModel.isPublishedAfterGeneration == null && shiftInStore.isPublishedAfterGeneration != null) {
                    shiftModel.isPublishedAfterGeneration = shiftInStore.isPublishedAfterGeneration;
                }
            }
            updateShiftInStore(shiftModel, shiftStoreEntity);
        }
    });

    viewStates.forEach((viewState) => {
        if (shiftsAdded.length || shiftsUpdated.length || shiftsDeleted.length) {
            // new stachel v3 action
            shiftsModified(shiftsAdded, shiftsDeleted, shiftsUpdated, viewState);
        }
        addEligibleShiftsToView({ viewState, shiftsToProcess, shiftStoreEntity });
    });
});